import { type Dispatch, type SetStateAction } from 'react'

export const ENROLLMENT_JOB_TYPE = 'Tasks::EnrollmentJob'
export const PAYMENT_JOB_TYPE = 'Tasks::PaymentJob'
export const DEPENDENT_CHANGE_JOB_TYPE = 'Tasks::DependentChangeJob'
export const BINDER_JOB_TYPE = 'Tasks::BinderJob'

/**
 * Maps certain carrier names to enrollment categories.
 * Special carriers (VSP, Guardian, Renaissance) should be filtered by category,
 * while others should be filtered by carrier name.
 */
const CARRIER_TO_CATEGORY_MAP: Record<string, string> = {
  VSP: 'vsp',
  Guardian: 'guardian_dental',
  Renaissance: 'renaissance_dental',
}

/**
 * Returns the enrollment category for carriers that use category-based filtering,
 * or undefined if the carrier should use carrier name filtering instead.
 */
export function getCarrierCategory (carrierName: string): string | undefined {
  return CARRIER_TO_CATEGORY_MAP[carrierName]
}

/**
 * Returns the appropriate filter key and value for a carrier name.
 * Special carriers use enrollment_categories filter, others use carrier_names filter.
 */
export function getCarrierFilterInfo (carrierName: string): {
  key: 'enrollment_categories' | 'carrier_names'
  value: string
} {
  const category = getCarrierCategory(carrierName)
  if (category) {
    return { key: 'enrollment_categories', value: category }
  } else {
    return { key: 'carrier_names', value: carrierName }
  }
}

/**
 * Creates a function to add a value to an array filter.
 * Only adds the value if it's not already present in the array.
 *
 * @param setFilters - The state setter function for filters
 * @returns A function that adds a value to an array filter
 */
export function createAddArrayFilter<TFilters extends Record<string, unknown>> (
  setFilters: Dispatch<SetStateAction<TFilters>>
) {
  return (key: keyof TFilters, value: string) => {
    setFilters((prev) => {
      const existingValues = (prev[key] as string[] | undefined) || []
      // Only add if not already present
      if (existingValues.includes(value)) {
        return prev
      }
      return {
        ...prev,
        [key]: [...existingValues, value],
      }
    })
  }
}

/**
 * Creates a function to set a single value filter.
 *
 * @param setFilters - The state setter function for filters
 * @returns A function that sets a single value filter
 */
export function createSetSingleValueFilter<
  TFilters extends Record<string, unknown>
> (setFilters: Dispatch<SetStateAction<TFilters>>) {
  return (key: keyof TFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }))
  }
}
